import { yupResolver } from '@hookform/resolvers/yup';
import { fetchFoodaidStart, QuestionSequenceName } from 'api/foodaid/questions';
import IconCircle from 'assets/icons/circle.svg';
import Button from 'components/button-new';
import Input from 'components/form/input';
import { useConfig } from 'config';
import { PetContextValues } from 'contexts/pet';
import { useRouteContext } from 'contexts/route';
import { useSegmentationContext } from 'contexts/segmentation';
import useSequenceName from 'hooks/segmentation/use-sequence-name';
import { useRouter } from 'next/router';
import React, { useCallback, useMemo } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { PetType } from 'utils/foodaid/pet';
import foodAidValidationSchema from './validationSchema';

interface LazyLoadNameInputForm {
  petName: string;
}

interface LazyLoadNameInputProps {
  petType: PetContextValues;
  submitCallback?: () => Promise<void> | void;
  formProps?: React.DetailedHTMLProps<
    React.FormHTMLAttributes<HTMLFormElement>,
    HTMLFormElement
  >;
}

/**
 * Input to start foodAid.
 * Initialization of food aid is done within component and they are passed down to food aid page
 */
const LazyLoadNameInput: React.FC<LazyLoadNameInputProps> = ({
  petType,
  submitCallback,
  formProps,
}) => {
  const { foodaidEndpoint } = useConfig();
  const { isReady, push } = useRouter();
  /**
   * Set up validation
   */
  const { register, handleSubmit, formState } = useForm<LazyLoadNameInputForm>({
    defaultValues: {
      petName: '',
    },
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    resolver: yupResolver(foodAidValidationSchema),
  });

  const _petType = useMemo(
    () => (petType === 'dogs' ? PetType.dog : PetType.cat),
    [petType]
  );

  const { routePaths } = useRouteContext();
  const { segmentation } = useSegmentationContext();

  const sequenceName = useSequenceName({
    isReady,
    petType: _petType,
    routePaths,
    segmentation,
    sequenceId: undefined,
  });

  const onSubmit: SubmitHandler<LazyLoadNameInputForm> = useCallback(
    async ({ petName }) => {
      const { caseId } = await fetchFoodaidStart({
        url: foodaidEndpoint,
        petType: _petType,
        sequenceName: QuestionSequenceName.Foodaid,
      });

      submitCallback && (await submitCallback());

      push({
        pathname: '/futterberater/[petType]/[sequenceId]',
        query: {
          petName,
          caseId,
          petType: _petType,
          sequenceId: sequenceName,
        },
      });
    },
    [_petType, sequenceName, push, submitCallback]
  );

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      method="post"
      {...formProps}
      className="mb-1 flex gap-2"
    >
      <div className="h-[48px] w-full ">
        <Input
          type="text"
          name="petName"
          placeholder={
            petType === 'dogs'
              ? 'foodaid:lazy-input:pet-name-input-dog'
              : 'foodaid:lazy-input:pet-name-input-cat'
          }
          register={register}
          formState={formState}
        />
      </div>
      <div className="pb-4">
        <Button type="submit" size="base" className="h-[48px] w-[86px]">
          {formState.isSubmitting ? (
            <IconCircle className="h-6 w-6 animate-spin text-text-dark-bg-contrast-white" />
          ) : (
            <FormattedMessage id="foodaid:lazy-input:create-nutrition-plan" />
          )}
        </Button>
      </div>
    </form>
  );
};

export default LazyLoadNameInput;
